import React, { useEffect, useRef } from "react";

export const DynamicFullHeightContainer = ({
  children,
  footerHeight = 0,
  containerHeightPx,
  setContainerHeightPx,
}: {
  children: React.ReactNode;
  footerHeight?: number;
  containerHeightPx: number;
  setContainerHeightPx: (height: number) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let rafId: number;
    let stableFrames = 0;
    let lastHeight = -1;
    const STABLE_THRESHOLD = 10;

    // Resting distance from the top of the document to the container's top edge.
    //
    // We deliberately walk the offsetParent chain instead of using
    // getBoundingClientRect(): CSS transforms do NOT affect offsetTop/offsetParent
    // layout positions. During page transitions the routed content is translated
    // vertically by framer-motion, which would corrupt a rect-based reading and
    // make the height (and any tiles sized from it) reflow on every animation frame.
    // offsetTop gives the stable resting position, so the correct height is applied
    // on the first frame and stays constant while the page slides in.
    const getRestingTop = (el: HTMLElement | null) => {
      let top = 0;
      let node = el;
      while (node) {
        top += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      return top;
    };

    const calculateHeight = () => {
      if (containerRef.current) {
        const restingTop = getRestingTop(containerRef.current);
        // Convert the document-relative resting top to a viewport-relative top.
        const viewportTop = restingTop - window.scrollY;

        const remainingHeight = window.innerHeight - viewportTop - footerHeight;
        setContainerHeightPx(remainingHeight);
        return remainingHeight;
      }
      return -1;
    };

    // Re-measure for a few frames to absorb late layout shifts (fonts/images),
    // then stop. Because the measurement is transform-immune it settles within a
    // frame or two and does not chase the slide animation.
    const stabilizeLoop = () => {
      const h = calculateHeight();
      if (Math.abs(h - lastHeight) < 1) {
        stableFrames++;
      } else {
        stableFrames = 0;
      }
      lastHeight = h;

      if (stableFrames < STABLE_THRESHOLD) {
        rafId = requestAnimationFrame(stabilizeLoop);
      }
    };

    calculateHeight();
    rafId = requestAnimationFrame(stabilizeLoop);

    window.addEventListener("resize", calculateHeight);

    return () => {
      window.removeEventListener("resize", calculateHeight);
      cancelAnimationFrame(rafId);
    };
  }, [footerHeight, setContainerHeightPx]);

  return (
    <div
      ref={containerRef}
      style={{
        height: `${containerHeightPx}px`,
        overflow: "auto",
        position: "relative",
      }}
    >
      {children}
    </div>
  );
};
